<template>
	<div class="offers-display" @click="openBuyboxModal">
		<ProviderIcon
			v-for="offer in displayedOffers"
			:key="offer.id"
			class="offers-display__icon"
			:providerShortName="offer.package.shortName"
			:iconUrl="offer.package.icon"
			:lazy="false"
		/>

		<div v-if="displayMoreOffers" class="offers-display__icon offers__icon--more">+{{ moreOffersCount }}</div>
	</div>
</template>

<script setup lang="ts">
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { computed } from 'vue';
import { StreamingChartTitle } from './types';
import { useUserStore } from '@/stores/pinia/useUserStore';

const { setBuyboxPresentationType } = useUserStore();

interface OffersDisplayProps {
	title: StreamingChartTitle;
	isMobile?: boolean;
}
const props = withDefaults(defineProps<OffersDisplayProps>(), {
	isMobile: false,
});

const offers = computed(() => props.title.node.offers ?? []);
const cinemaOfferCount = computed(() => offers.value.filter(offer => offer.monetizationType === 'CINEMA').length);
const hasCinemaOffers = computed(() => cinemaOfferCount.value > 0);

const uniqueOffers = computed(() => [
	...new Map(
		offers.value.filter(offer => offer.monetizationType !== 'CINEMA').map(offer => [offer.package.packageId, offer])
	).values(),
]);
const visibleProvidersCount = computed(() => (props.isMobile ? 6 : 3));

const displayedOffers = computed(() => uniqueOffers.value.slice(0, visibleProvidersCount.value));

const displayMoreOffers = computed(() => {
	return (
		uniqueOffers.value.length - visibleProvidersCount.value > 0 ||
		(hasCinemaOffers.value && cinemaOfferCount.value > 0)
	);
});
const moreOffersCount = computed(() => {
	return hasCinemaOffers.value && uniqueOffers.value.length - visibleProvidersCount.value <= 0
		? cinemaOfferCount.value
		: uniqueOffers.value.length -
				visibleProvidersCount.value +
				(hasCinemaOffers.value ? cinemaOfferCount.value : 0);
});

async function openBuyboxModal() {
	const { default: WatchNowModal } = await import('@/components/modals/WatchNowModal.vue');
	setBuyboxPresentationType({ presentationType: null });
	ModalHelper.openModal(WatchNowModal, {
		title: props.title.node,
	});
}
</script>

<style lang="scss" scoped>
.offers-display {
	display: flex;
	align-items: center;

	&__icon {
		margin-inline-end: 8px;
		cursor: pointer;
		border: 1px solid transparent;

		:deep(img.provider-icon.square) {
			height: 32px;
			width: 32px;
			min-width: 32px;
		}

		&--more {
			border: 1px solid #6f7d90;
			border-radius: 20%;
			font-size: 12px;
			display: flex;
			justify-content: center;
			align-items: center;
			width: 32px;
			height: 32px;

			&:hover {
				background-color: #1c252f;
			}
		}
	}
}
</style>
